import { useMemo, useState } from 'react';

import {
    ImageLightbox,
    type LightboxSlide,
} from '@/components/site/image-lightbox';
import { SoftImage } from '@/components/site/soft-image';
import {
    mapEmbedUrl,
    splitParagraphContent,
    videoEmbedUrl,
    type ArticleSection,
} from '@/lib/article-body';
import { renderInlineMarkdown } from '@/lib/article-inline-format';
import type { ContentImage } from '@/lib/content-types';
import { filePreviewUrl } from '@/lib/media-url';
import { cn } from '@/lib/utils';

/** Width-only preview so Appwrite does not center-crop the subject. */
function galleryPreviewSrc(image: ContentImage): string | undefined {
    if (image.fileId) {
        return (
            filePreviewUrl(image.fileId, {
                width: 900,
                quality: 75,
                output: 'avif',
            }) ?? image.url
        );
    }
    return image.url;
}

function SectionCaption({
    caption,
    credit,
}: {
    caption?: string;
    credit?: string;
}) {
    if (!caption && !credit) return null;
    return (
        <figcaption>{[caption, credit].filter(Boolean).join(' · ')}</figcaption>
    );
}

function FormattedText({ text }: { text: string }) {
    return <>{renderInlineMarkdown(text)}</>;
}

function ParagraphBlock({ content }: { content: string }) {
    const parts = splitParagraphContent(content);
    if (parts.length === 0) return null;
    return (
        <>
            {parts.map((p, i) => (
                <p key={i}>
                    <FormattedText text={p} />
                </p>
            ))}
        </>
    );
}

function ImageBlock({
    section,
    image,
}: {
    section: Extract<ArticleSection, { type: 'image' }>;
    image?: ContentImage;
}) {
    const [open, setOpen] = useState(false);
    const url = image?.url;
    const caption = section.caption || image?.caption;
    const credit = section.credit || image?.credit;
    const alt = caption || image?.alt || 'תמונה';
    const slides: LightboxSlide[] = url
        ? [
              {
                  src: url,
                  fileId: image?.fileId,
                  alt,
                  caption,
                  credit,
              },
          ]
        : [];

    if (!url) return null;

    return (
        <figure className="article-figure">
            <button
                type="button"
                className="article-lightbox-trigger image-frame aspect-video w-full"
                onClick={() => setOpen(true)}
                aria-label="הגדלת תמונה"
            >
                <SoftImage
                    src={url}
                    alt={alt}
                    width={1100}
                    height={619}
                    sizes="(max-width: 768px) 100vw, 720px"
                    className="size-full object-cover"
                    loading="lazy"
                />
            </button>
            <SectionCaption caption={caption} credit={credit} />
            <ImageLightbox
                open={open}
                onOpenChange={setOpen}
                slides={slides}
                index={0}
            />
        </figure>
    );
}

function GalleryBlock({
    section,
    imagesById,
}: {
    section: Extract<ArticleSection, { type: 'gallery' }>;
    imagesById: Record<string, ContentImage>;
}) {
    const [open, setOpen] = useState(false);
    const [index, setIndex] = useState(0);

    const slides = useMemo((): LightboxSlide[] => {
        const out: LightboxSlide[] = [];
        section.imageIds.forEach((id, i) => {
            const image = imagesById[id];
            const src = image ? galleryPreviewSrc(image) : undefined;
            if (!image || !src) return;
            out.push({
                src,
                fileId: image.fileId,
                alt:
                    image.caption ||
                    image.alt ||
                    section.caption ||
                    `תמונה ${i + 1}`,
                caption: image.caption,
                credit: image.credit,
            });
        });
        return out;
    }, [section.imageIds, section.caption, imagesById]);

    if (slides.length === 0) return null;

    const count = slides.length;
    // 6-track grid on md lets leftover rows fill evenly (2× half, or 1 centered).
    // 4 images stay as a clean 2×2. Mobile is 2-col with a full-width odd last tile.
    const gridClass =
        count === 1
            ? 'grid-cols-1'
            : count === 2 || count === 4
              ? 'grid-cols-2'
              : 'grid-cols-2 md:grid-cols-6';

    function itemClass(i: number): string {
        if (count <= 2 || count === 4) return '';

        // Mobile 2-col: odd last tile spans full width.
        const mobileOddLast =
            count % 2 === 1 && i === count - 1 ? 'col-span-2' : '';

        // Desktop 3-across via 6 tracks (span 2 each).
        const rem = count % 3;
        if (rem === 1 && i === count - 1) {
            // One leftover: centered, a bit wider than a normal cell.
            return cn(mobileOddLast, 'md:col-span-4 md:col-start-2');
        }
        if (rem === 2 && i >= count - 2) {
            // Two leftovers: equal halves, no empty cell.
            return cn(mobileOddLast, 'md:col-span-3');
        }
        return cn(mobileOddLast, 'md:col-span-2');
    }

    return (
        <figure className="article-gallery">
            <div
                className={cn(
                    'article-gallery-grid grid gap-2 md:gap-3',
                    gridClass,
                )}
            >
                {slides.map((slide, i) => (
                    <button
                        key={`${slide.fileId ?? slide.src}-${i}`}
                        type="button"
                        className={cn(
                            'article-lightbox-trigger image-frame relative aspect-[4/3] overflow-hidden bg-white/[0.03]',
                            itemClass(i),
                        )}
                        onClick={() => {
                            setIndex(i);
                            setOpen(true);
                        }}
                        aria-label={`הגדלת תמונה ${i + 1}`}
                    >
                        <SoftImage
                            src={slide.src}
                            alt={slide.alt}
                            width={900}
                            sizes="(max-width: 768px) 50vw, 33vw"
                            className="absolute inset-0 size-full object-contain"
                            loading="lazy"
                        />
                    </button>
                ))}
            </div>
            <SectionCaption caption={section.caption} />
            <ImageLightbox
                open={open}
                onOpenChange={setOpen}
                slides={slides}
                index={index}
                onIndexChange={setIndex}
            />
        </figure>
    );
}

function VideoBlock({
    section,
}: {
    section: Extract<ArticleSection, { type: 'video' }>;
}) {
    const embed = videoEmbedUrl(section.url);
    if (!embed) {
        return (
            <p className="my-6">
                <a href={section.url} target="_blank" rel="noopener noreferrer">
                    {section.caption || section.url}
                </a>
            </p>
        );
    }
    return (
        <figure>
            <div className="aspect-video w-full overflow-hidden bg-black">
                <iframe
                    src={embed}
                    title={section.caption || 'Embedded video'}
                    className="size-full"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    loading="lazy"
                />
            </div>
            <SectionCaption caption={section.caption} />
        </figure>
    );
}

function MapBlock({
    section,
}: {
    section: Extract<ArticleSection, { type: 'map' }>;
}) {
    return (
        <figure>
            <div className="aspect-video w-full overflow-hidden bg-white/[0.03]">
                <iframe
                    src={mapEmbedUrl(section.address)}
                    title={section.caption || section.address}
                    className="size-full border-0"
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                />
            </div>
            <SectionCaption
                caption={section.caption || section.address}
            />
        </figure>
    );
}

export function ArticleBodyView({
    sections,
    imagesById,
}: {
    sections: ArticleSection[];
    imagesById: Record<string, ContentImage>;
}) {
    if (!sections.length) return null;

    // Fragment (not a wrapper div) so `.prose-grid`'s space-y and `> p`
    // selectors apply to each section as a direct child.
    return (
        <>
            {sections.map((section, index) => {
                const key = `${section.type}-${index}`;
                switch (section.type) {
                    case 'paragraph':
                        return <ParagraphBlock key={key} content={section.content} />;
                    case 'title':
                        return (
                            <h2 key={key} className="title">
                                <FormattedText text={section.title} />
                            </h2>
                        );
                    case 'quote':
                        return (
                            <blockquote key={key}>
                                <FormattedText text={section.quote} />
                                {section.speaker ? (
                                    <cite>{section.speaker}</cite>
                                ) : null}
                            </blockquote>
                        );
                    case 'link':
                        return (
                            <p key={key}>
                                <a
                                    className="link"
                                    href={section.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    {section.label || section.url}
                                </a>
                            </p>
                        );
                    case 'image':
                        return (
                            <ImageBlock
                                key={key}
                                section={section}
                                image={imagesById[section.imageId]}
                            />
                        );
                    case 'gallery':
                        return (
                            <GalleryBlock
                                key={key}
                                section={section}
                                imagesById={imagesById}
                            />
                        );
                    case 'video':
                        return <VideoBlock key={key} section={section} />;
                    case 'map':
                        return <MapBlock key={key} section={section} />;
                    default:
                        return null;
                }
            })}
        </>
    );
}
